import React, { createContext, useContext, useState, useEffect } from 'react';

// 5 Thèmes disponibles
export const themes = {
  original: {
    id: 'original',
    name: 'Bleu Profond',
    description: 'Thème original sombre',
    icon: '🌙',
    colors: {
      // Backgrounds
      bgPrimary: 'bg-slate-950',
      bgSecondary: 'bg-slate-900',
      bgTertiary: 'bg-slate-800',
      bgCard: 'bg-slate-900/50',
      bgHover: 'hover:bg-slate-800',
      bgInput: 'bg-slate-800/50',
      
      // Borders
      border: 'border-slate-800',
      borderHover: 'hover:border-slate-700',
      
      // Text
      textPrimary: 'text-white',
      textSecondary: 'text-slate-300',
      textMuted: 'text-slate-400',
      textAccent: 'text-blue-400',
      
      // Semantic colors
      textWarning: 'text-amber-400',
      bgWarning: 'bg-amber-500/20',
      textDanger: 'text-rose-400',
      textSuccess: 'text-emerald-400',
      
      // Logo text (adapts per theme)
      logoText: 'text-white',
      
      // Accent colors
      accent: 'bg-blue-600',
      accentHover: 'hover:bg-blue-700',
      accentText: 'text-blue-400',
      
      // Sidebar
      sidebarBg: 'bg-slate-950/95',
      sidebarBorder: 'border-slate-800',
      sidebarActive: 'bg-blue-600/20 text-blue-400',
      
      // Gradients
      gradientFrom: 'from-blue-600/20',
      gradientTo: 'to-indigo-600/20',
      
      // CSS Variables
      cssVars: {
        '--bg-primary': '#020617',
        '--bg-secondary': '#0f172a',
        '--bg-tertiary': '#1e293b',
        '--text-primary': '#ffffff',
        '--text-secondary': '#cbd5e1',
        '--accent': '#2563eb',
        '--border': '#1e293b',
      }
    }
  },
  
  light: {
    id: 'light',
    name: 'Blanc Clinique',
    description: 'Thème clair médical',
    icon: '☀️',
    colors: {
      bgPrimary: 'bg-white',
      bgSecondary: 'bg-white',
      bgTertiary: 'bg-white',
      bgCard: 'bg-white',
      bgHover: 'hover:bg-blue-50',
      bgInput: 'bg-white',
      
      border: 'border-slate-200',
      borderHover: 'hover:border-slate-300',
      
      textPrimary: 'text-slate-900',
      textSecondary: 'text-slate-700',
      textMuted: 'text-slate-500',
      textAccent: 'text-blue-600',
      
      // Semantic colors (darker for light theme)
      textWarning: 'text-amber-600',
      bgWarning: 'bg-amber-50',
      textDanger: 'text-rose-600',
      textSuccess: 'text-emerald-600',
      
      // Logo text
      logoText: 'text-slate-900',
      
      accent: 'bg-blue-600',
      accentHover: 'hover:bg-blue-700',
      accentText: 'text-blue-600',
      
      sidebarBg: 'bg-white',
      sidebarBorder: 'border-slate-200',
      sidebarActive: 'bg-blue-50 text-blue-600',
      
      gradientFrom: 'from-blue-50',
      gradientTo: 'to-indigo-50',
      
      cssVars: {
        '--bg-primary': '#ffffff',
        '--bg-secondary': '#ffffff',
        '--bg-tertiary': '#ffffff',
        '--text-primary': '#0f172a',
        '--text-secondary': '#334155',
        '--accent': '#2563eb',
        '--border': '#e2e8f0',
      }
    }
  },
  
  emerald: {
    id: 'emerald',
    name: 'Émeraude Santé',
    description: 'Thème vert médical',
    icon: '💚',
    colors: {
      bgPrimary: 'bg-emerald-950',
      bgSecondary: 'bg-emerald-900',
      bgTertiary: 'bg-emerald-800',
      bgCard: 'bg-emerald-900/50',
      bgHover: 'hover:bg-emerald-800',
      bgInput: 'bg-emerald-800/50',
      
      border: 'border-emerald-800',
      borderHover: 'hover:border-emerald-700',
      
      textPrimary: 'text-white',
      textSecondary: 'text-emerald-200',
      textMuted: 'text-emerald-300/60',
      textAccent: 'text-emerald-400',
      
      // Semantic colors
      textWarning: 'text-amber-400',
      bgWarning: 'bg-amber-500/20',
      textDanger: 'text-rose-400',
      textSuccess: 'text-teal-400',
      
      // Logo text
      logoText: 'text-white',
      
      accent: 'bg-emerald-600',
      accentHover: 'hover:bg-emerald-700',
      accentText: 'text-emerald-400',
      
      sidebarBg: 'bg-emerald-950/95',
      sidebarBorder: 'border-emerald-800',
      sidebarActive: 'bg-emerald-600/20 text-emerald-400',
      
      gradientFrom: 'from-emerald-600/20',
      gradientTo: 'to-teal-600/20',
      
      cssVars: {
        '--bg-primary': '#022c22',
        '--bg-secondary': '#064e3b',
        '--bg-tertiary': '#065f46',
        '--text-primary': '#ffffff',
        '--text-secondary': '#a7f3d0',
        '--accent': '#059669',
        '--border': '#065f46',
      }
    }
  },
  
  purple: {
    id: 'purple',
    name: 'Violet Élégant',
    description: 'Thème violet premium',
    icon: '💜',
    colors: {
      bgPrimary: 'bg-violet-950',
      bgSecondary: 'bg-violet-900',
      bgTertiary: 'bg-violet-800',
      bgCard: 'bg-violet-900/50',
      bgHover: 'hover:bg-violet-800',
      bgInput: 'bg-violet-800/50',
      
      border: 'border-violet-800',
      borderHover: 'hover:border-violet-700',
      
      textPrimary: 'text-white',
      textSecondary: 'text-violet-200',
      textMuted: 'text-violet-300/60',
      textAccent: 'text-violet-400',
      
      // Semantic colors
      textWarning: 'text-amber-400',
      bgWarning: 'bg-amber-500/20',
      textDanger: 'text-rose-400',
      textSuccess: 'text-emerald-400',
      
      // Logo text
      logoText: 'text-white',
      
      accent: 'bg-violet-600',
      accentHover: 'hover:bg-violet-700',
      accentText: 'text-violet-400',
      
      sidebarBg: 'bg-violet-950/95',
      sidebarBorder: 'border-violet-800',
      sidebarActive: 'bg-violet-600/20 text-violet-400',
      
      gradientFrom: 'from-violet-600/20',
      gradientTo: 'to-purple-600/20',
      
      cssVars: {
        '--bg-primary': '#2e1065',
        '--bg-secondary': '#4c1d95',
        '--bg-tertiary': '#5b21b6',
        '--text-primary': '#ffffff',
        '--text-secondary': '#ddd6fe',
        '--accent': '#7c3aed',
        '--border': '#5b21b6',
      }
    }
  },
  
  midnight: {
    id: 'midnight',
    name: 'Minuit Noir',
    description: 'Thème OLED ultra-sombre',
    icon: '🖤',
    colors: {
      bgPrimary: 'bg-black',
      bgSecondary: 'bg-zinc-950',
      bgTertiary: 'bg-zinc-900',
      bgCard: 'bg-zinc-950/80',
      bgHover: 'hover:bg-zinc-900',
      bgInput: 'bg-zinc-900/50',
      
      border: 'border-zinc-800',
      borderHover: 'hover:border-zinc-700',
      
      textPrimary: 'text-white',
      textSecondary: 'text-zinc-300',
      textMuted: 'text-zinc-500',
      textAccent: 'text-cyan-400',
      
      // Semantic colors
      textWarning: 'text-amber-400',
      bgWarning: 'bg-amber-500/20',
      textDanger: 'text-rose-400',
      textSuccess: 'text-emerald-400',
      
      // Logo text
      logoText: 'text-white',
      
      accent: 'bg-cyan-600',
      accentHover: 'hover:bg-cyan-700',
      accentText: 'text-cyan-400',
      
      sidebarBg: 'bg-black',
      sidebarBorder: 'border-zinc-800',
      sidebarActive: 'bg-cyan-600/20 text-cyan-400',
      
      gradientFrom: 'from-cyan-600/10',
      gradientTo: 'to-cyan-600/10',
      
      cssVars: {
        '--bg-primary': '#000000',
        '--bg-secondary': '#09090b',
        '--bg-tertiary': '#18181b',
        '--text-primary': '#ffffff',
        '--text-secondary': '#d4d4d8',
        '--accent': '#0891b2',
        '--border': '#27272a',
      }
    }
  }
};

const ThemeContext = createContext();

export const ThemeProvider = ({ children }) => {
  const [currentTheme, setCurrentTheme] = useState(() => {
    const saved = localStorage.getItem('hippocrate_theme');
    return saved && themes[saved] ? saved : 'original';
  });

  const theme = themes[currentTheme];

  useEffect(() => {
    localStorage.setItem('hippocrate_theme', currentTheme);
    
    // Apply CSS variables to root
    const root = document.documentElement;
    Object.entries(theme.colors.cssVars).forEach(([key, value]) => {
      root.style.setProperty(key, value);
    });
    
    // Set data attribute for global CSS targeting
    document.body.setAttribute('data-theme', currentTheme);
  }, [currentTheme, theme]);

  const setTheme = (themeId) => {
    if (themes[themeId]) {
      setCurrentTheme(themeId);
    }
  };

  return (
    <ThemeContext.Provider value={{ 
      theme, 
      currentTheme, 
      setTheme, 
      themes,
      // Helper function to get color class
      c: (colorKey) => theme.colors[colorKey] || ''
    }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export default ThemeContext;
